import React, {
  FC,
  PropsWithChildren,
  ReactElement,
  useMemo,
  useState,
} from 'react';
import { Button, ButtonProps, Divider, Dropdown, MenuProps } from 'antd';
import { MoreVertical } from 'react-feather';
import styled from 'styled-components';
import { SubmitHandler } from 'react-hook-form';

import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Main } from './Main';
import { Stack } from './Stack';
import { SidePanel } from './SidePanel';
import { FormSubmitButton } from '../form';

import translations from './DetailLayout.translations.json';
import { BookmarkCreate } from './BookmarkCreate';
import { InfoIcon } from './InfoIcon';
import { InfoIconUrl } from './InfoIconUrl';

type DetailLayoutActionProps = {
  id?: string;
  label?: string;
  type?: 'mainAction' | 'abort' | 'formSubmit';
  layoutType?: 'mutate' | 'detail';
  onClick?: ButtonProps['onClick'] | SubmitHandler<any>;
  buttonType?: ButtonProps['type'];
} & Omit<ButtonProps, 'id' | 'type' | 'onClick'>;

export const ActionButton: FC<DetailLayoutActionProps> = ({
  children,
  label,
  type = 'mainAction',
  id = type,
  layoutType = 'mutate',
  buttonType,
  ...rest
}) => {
  const { t } = useTranslation();

  const buttonText = useMemo(() => {
    if (label) {
      return label;
    }
    switch (type) {
      case 'formSubmit':
      case 'mainAction':
        return t(`button.${layoutType === 'mutate' ? 'save' : 'edit'}`);
      case 'abort':
        return t(`button.${layoutType === 'mutate' ? 'abort' : 'close'}`);
      default:
        return '';
    }
  }, [layoutType, label, t, type]);

  if (type === 'formSubmit') {
    return (
      <FormSubmitButton id={id} buttonType={buttonType} {...rest}>
        {buttonText}
      </FormSubmitButton>
    );
  }

  return (
    <Button
      id={id}
      type={buttonType || (type === 'mainAction' ? 'primary' : 'default')}
      {...rest}
    >
      {children || buttonText}
    </Button>
  );
};

type SideBar = {
  footer?: ReactElement;
  content?: ReactElement;
  header?: ReactElement;
};

type HeaderConfig = {
  dropdownMenu?: MenuProps;
  actions?: ReactElement;
};

export type DetailLayoutProps = {
  headerConfig?: HeaderConfig;
  breadCrumb: ReactElement;
  bookmarkCreate?: ReactElement;
  infoIconUrl?: InfoIconUrl;
  type?: 'create' | 'edit' | 'detail';
  side?: SideBar;
  footerContent?: ReactElement;
  defaultWidth?: number;
  alert?: ReactElement;
} & PropsWithChildren;

const { addTranslations, useTranslation } = i18nNsFactory('detail-layout');
addTranslations(translations);

export const DetailLayout: FC<DetailLayoutProps> = ({
  children,
  side,
  headerConfig,
  breadCrumb,
  bookmarkCreate,
  infoIconUrl,
  footerContent,
  defaultWidth = 450,
  alert,
}) => {
  const { dropdownMenu, actions } = headerConfig || ({} as HeaderConfig);
  const [sideWidth, setSideWidth] = useState(defaultWidth);

  return (
    <Main>
      <Main.Col>
        <Main.Header>
          {breadCrumb}
          <ButtonsWrapper
            direction="horizontal"
            size="sm"
            data-testid="detailActions"
          >
            {infoIconUrl && <InfoIcon infoIconUrl={infoIconUrl}></InfoIcon>}
            {bookmarkCreate ?? <BookmarkCreate />}
            {actions}
            {dropdownMenu && (
              <Dropdown menu={dropdownMenu} trigger={['click']}>
                <Button
                  id="detail-layout-header-dropdown-menu"
                  type="text"
                  shape="circle"
                  icon={<MoreVertical />}
                />
              </Dropdown>
            )}
          </ButtonsWrapper>
        </Main.Header>
        <Main.Content padding={false} scrollable={false}>
          <Main style={{ position: 'relative' }}>
            {side && (
              <SidePanel
                width={defaultWidth}
                defaultCollapsed={false}
                onResize={setSideWidth}
              >
                <SidePanelWrapper data-testid="detailSidePanel">
                  <div>
                    {side.header}
                    {side.header && <Divider style={{ margin: 0 }} />}
                  </div>
                  <SidePanelContentWrapper>
                    {side.content}
                  </SidePanelContentWrapper>
                  <Main.Footer>{side?.footer}</Main.Footer>
                </SidePanelWrapper>
              </SidePanel>
            )}
            <Main.Col
              border={!!side}
              style={
                side ? { flexBasis: `calc(100% - ${sideWidth}px)` } : undefined
              }
            >
              {alert}
              <Main.Content padding={false} id="detail-layout-main-content">
                {children}
              </Main.Content>
              <Main.Footer>{footerContent}</Main.Footer>
            </Main.Col>
          </Main>
        </Main.Content>
      </Main.Col>
    </Main>
  );
};

const ButtonsWrapper = styled(Stack)`
  margin-left: auto;
  align-items: center;
`;

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const SidePanelContentWrapper = styled.div`
  min-width: 300px;
  overflow-y: auto;
  overflow-x: hidden;
`;
